import { withRouter } from "react-router-dom";
import { withNamespaces } from "react-i18next";
import { useContext, useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button, Card, CardBody, CardHeader, Spinner, UncontrolledTooltip } from "reactstrap";
import DocumentManagementService from "src/modules/3rd-party-management/apis/admin/DocumentManagementService";
import DocumentModal from "src/modules/3rd-party-management/components/document-management/components/DocumentModal";
import DeleteDocumentModal from "src/modules/3rd-party-management/components/document-management/components/DeleteDocumentModal";
import { ThirdPartyDocumentCategories, ThirdPartyDocumentTypes } from "src/modules/3rd-party-management/constants";
import ProductContext from "../../../../contexts/ProductContext";

const optionTitle = (options, value) => (
    options.find((option) => option.value === value)?.title || value || "---"
);

const documentContent = (document) => {
    const contentType = document.content_type || document.contentType;

    if (contentType === "link") {
        return document.path || "---";
    }

    return document.name || document.originName || "---";
};

const Documents = ({
    t,
    onNextButtonClicked,
    onBackButtonClicked,
    onCancelButtonClicked
}) => {
    const {
        isEditingProduct,
        editingProductId,
        supplierId,
        productData,
        fetchingDetails,
        linkedDocuments,
        setLinkedDocuments,
        setLoadedDocumentRows,
        originalDocumentIds,
        setOriginalDocumentIds,
        canLinkDocuments,
        setCanLinkDocuments,
        documentsInitialized,
        setDocumentsInitialized
    } = useContext(ProductContext);

    const [showDocumentModal, setShowDocumentModal] = useState(false);
    const [documentToEdit, setDocumentToEdit] = useState(null);
    const [documentToDelete, setDocumentToDelete] = useState(null);

    const documentsQuery = useQuery({
        queryKey: [
            '3rd-party-management-fetch-documents-management-list',
            null,
            editingProductId || null
        ],
        queryFn: async () => {
            const service = DocumentManagementService.getInstance();

            return await service.listByProduct(editingProductId, {
                pageIndex: 1,
                pageSize: 1000,
            });
        },
        enabled: isEditingProduct && !!editingProductId,
        refetchOnWindowFocus: false,
        retry: false,
    });

    useEffect(() => {
        if (
            !isEditingProduct
            || documentsInitialized
            || fetchingDetails
            || !productData?.id
            || !documentsQuery.isSuccess
        ) {
            return;
        }

        const rows = Array.isArray(documentsQuery.data?.documents)
            ? documentsQuery.data.documents
            : [];
        const attachedIds = Array.isArray(productData.documentIds)
            ? productData.documentIds.map((id) => String(id))
            : [];
        const visibleIds = new Set(rows.map((row) => String(row.id)));
        const reportedCount = Number(documentsQuery.data?.itemsCount);
        const loadedEveryRow = !Number.isFinite(reportedCount) || reportedCount <= rows.length;

        setOriginalDocumentIds(attachedIds);
        setCanLinkDocuments(
            loadedEveryRow && attachedIds.every((id) => visibleIds.has(id))
        );
        setLinkedDocuments(rows);
        setLoadedDocumentRows(rows);
        setDocumentsInitialized(true);
    }, [
        isEditingProduct,
        documentsInitialized,
        fetchingDetails,
        productData?.id,
        productData?.documentIds,
        documentsQuery.isSuccess,
        documentsQuery.data,
        setOriginalDocumentIds,
        setCanLinkDocuments,
        setLinkedDocuments,
        setLoadedDocumentRows,
        setDocumentsInitialized
    ]);

    const linksCanChange = !isEditingProduct || canLinkDocuments;
    const isLoadingDocuments = isEditingProduct && !documentsInitialized && !documentsQuery.isError;

    const closeDocumentModal = () => {
        setShowDocumentModal(false);
        setDocumentToEdit(null);
    };

    const saveDocument = async (formData, currentDocument) => {
        const service = DocumentManagementService.getInstance();

        if (currentDocument?.id) {
            const updated = await service.update(currentDocument.id, formData);

            setLinkedDocuments((currentDocuments) => currentDocuments.map((document) => (
                String(document.id) === String(currentDocument.id)
                    ? {
                        ...document,
                        ...(updated || {}),
                        id: currentDocument.id,
                        title: updated?.title || formData.get('title') || document.title,
                        type: updated?.type || formData.get('type') || document.type,
                        category: updated?.category || formData.get('category') || document.category,
                        content_type: updated?.content_type || formData.get('contentType') || document.content_type,
                        path: updated?.path || formData.get('path') || document.path,
                        confidentialityLevel: updated?.confidentialityLevel
                            || formData.get('confidentialityLevel')
                            || document.confidentialityLevel,
                        description: updated?.description
                            ?? formData.get('description')
                            ?? document.description,
                        ...(formData.get('file') instanceof File ? {
                            name: updated?.name || formData.get('file').name,
                            size: formData.get('file').size,
                        } : {}),
                    }
                    : document
            )));

            return;
        }

        const created = await service.create(formData);
        const id = created?.id;

        if (!id) {
            throw new Error('Document id was missing.');
        }

        const file = formData.get('file');

        setLinkedDocuments((currentDocuments) => [
            ...currentDocuments,
            {
                ...(created || {}),
                id,
                title: created?.title || formData.get('title') || '',
                type: created?.type || formData.get('type') || '',
                category: created?.category || formData.get('category') || '',
                content_type: created?.content_type || formData.get('contentType') || 'file',
                path: created?.path || formData.get('path') || '',
                name: created?.name || (file instanceof File ? file.name : ''),
                size: file instanceof File ? file.size : created?.size,
                confidentialityLevel: created?.confidentialityLevel
                    || formData.get('confidentialityLevel')
                    || '',
                description: created?.description ?? formData.get('description') ?? '',
            }
        ]);
    };

    const removeDocument = async (document) => {
        const isAttached = (originalDocumentIds || [])
            .map((id) => String(id))
            .includes(String(document.id));

        if (!isAttached) {
            const service = DocumentManagementService.getInstance();

            await service.delete(document.id);
        }

        setLinkedDocuments((currentDocuments) => currentDocuments.filter((item) => (
            String(item.id) !== String(document.id)
        )));
    };

    const removingAttachedDocument = documentToDelete
        && (originalDocumentIds || []).map((id) => String(id)).includes(String(documentToDelete.id));

    const description = (() => {
        if (!isEditingProduct) {
            return t("Add documents for this product. Each one is uploaded now and linked when you confirm. Removing one deletes it.");
        }

        if (!documentsInitialized) {
            return t("Manage the documents linked to this product.");
        }

        if (!canLinkDocuments) {
            return t("Some documents on this product are not visible to you, so document links cannot be changed here.");
        }

        return t("New documents are uploaded now. Removing a document already linked to this product deletes it when you save.");
    })();

    return (
        <Card>
            <CardHeader className="bg-white">
                <h5 className="mb-1">
                    {t("Documents")}
                </h5>
                <p className="text-muted mb-0">
                    {description}
                </p>
            </CardHeader>
            <CardBody>
                {isLoadingDocuments ? (
                    <div className="text-center py-4">
                        <Spinner color="primary" />
                        <p className="text-muted mt-2 mb-0">
                            {t("Loading documents...")}
                        </p>
                    </div>
                ) : (
                    <>
                        {documentsQuery.isError && (
                            <div className="alert alert-danger" role="alert">
                                {t("An error occurred while loading documents.")}
                            </div>
                        )}

                        {linksCanChange && (
                            <div className="d-flex justify-content-end mb-3">
                                <Button
                                    color="primary"
                                    outline
                                    type="button"
                                    onClick={() => setShowDocumentModal(true)}
                                >
                                    {t("Add Document")}
                                </Button>
                            </div>
                        )}

                        {linkedDocuments.length === 0 ? (
                            <div className="alert m-0" role="alert">
                                <p className="text-center mb-0">
                                    {t("No documents added yet. You can continue without documents.")}
                                </p>
                            </div>
                        ) : (
                            <div className="table-responsive">
                                <table className="table tpdd-table">
                                    <thead className="thead-light">
                                        <tr>
                                            <th>{t("Title")}</th>
                                            <th>{t("Type")}</th>
                                            <th>{t("Category")}</th>
                                            <th>{t("Content")}</th>
                                            {linksCanChange && <th>{t("Actions")}</th>}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {linkedDocuments.map((document) => (
                                            <tr key={document.id}>
                                                <td>{document.title}</td>
                                                <td>{t(optionTitle(ThirdPartyDocumentTypes, document.type))}</td>
                                                <td>{t(optionTitle(ThirdPartyDocumentCategories, document.category))}</td>
                                                <td>{documentContent(document)}</td>
                                                {linksCanChange && (
                                                    <td>
                                                        <div className="d-flex align-items-center">
                                                            <Button
                                                                id={`delete-product-document-${document.id}`}
                                                                color="link"
                                                                className="waves-effect me-1 text-danger"
                                                                type="button"
                                                                onClick={() => setDocumentToDelete(document)}
                                                            >
                                                                <i className="ri-delete-bin-line font-size-22" />
                                                            </Button>
                                                            <UncontrolledTooltip
                                                                placement="top"
                                                                target={`delete-product-document-${document.id}`}
                                                            >
                                                                {t("Delete Document")}
                                                            </UncontrolledTooltip>

                                                            <Button
                                                                id={`edit-product-document-${document.id}`}
                                                                color="primary"
                                                                outline
                                                                className="border-0 waves-effect"
                                                                type="button"
                                                                onClick={() => setDocumentToEdit(document)}
                                                            >
                                                                <i className="ri-pencil-line font-size-22" />
                                                            </Button>
                                                            <UncontrolledTooltip
                                                                placement="top"
                                                                target={`edit-product-document-${document.id}`}
                                                            >
                                                                {t("Edit Document")}
                                                            </UncontrolledTooltip>
                                                        </div>
                                                    </td>
                                                )}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        <DocumentModal
                            isOpen={showDocumentModal || !!documentToEdit}
                            toggle={closeDocumentModal}
                            onSubmit={saveDocument}
                            documentToEdit={documentToEdit}
                            supplierId={supplierId}
                        />

                        <DeleteDocumentModal
                            isOpen={!!documentToDelete}
                            toggle={() => setDocumentToDelete(null)}
                            onDelete={removeDocument}
                            document={documentToDelete}
                            successMessage={removingAttachedDocument
                                ? t("This document will be removed when you save the product.")
                                : undefined}
                        />
                    </>
                )}

                <div className="d-flex justify-content-between mt-3">
                    <Button
                        color="secondary"
                        outline
                        type="button"
                        onClick={onCancelButtonClicked}
                    >
                        {t("Cancel")}
                    </Button>

                    <div>
                        <Button
                            color="secondary"
                            onClick={onBackButtonClicked}
                            className="me-2"
                            type="button"
                        >
                            {t("Back")}
                        </Button>

                        <Button
                            color="primary"
                            type="button"
                            onClick={onNextButtonClicked}
                            disabled={isLoadingDocuments}
                        >
                            {t("Next")}
                        </Button>
                    </div>
                </div>
            </CardBody>
        </Card>
    );
};

export default withNamespaces()(
    withRouter(Documents)
);
